// Parcours de configuration du tracking, en 4 étapes.
// Chaque étape est sauvegardée côté serveur dès validation : l'utilisateur peut
// fermer l'onglet et revenir plus tard, on le ramène automatiquement à la
// première étape non terminée (resumeStep). C'est indispensable ici — aller
// chercher un token CAPI chez Meta demande de sortir de l'app.
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { FaFacebookF, FaTiktok, FaSnapchatGhost, FaGoogle, FaLinkedinIn } from "react-icons/fa";
import {
  SETUP_STEPS, SITE_PLATFORMS, EVENT_CATALOG,
  fetchSetup, fetchSetupContext, saveSetupSite, saveSetupPixel,
  validateSetupPixel, saveSetupEvents, verifySetup, skipSetupStep, resetSetup,
} from "../../services/trackingService";

const BRAND = "#221C51";

const PLATFORM_LABEL = { meta: "Meta (Facebook / Instagram)", tiktok: "TikTok", snapchat: "Snapchat", google: "Google Ads", linkedin: "LinkedIn" };

// Identité visuelle par régie : icône + couleurs de marque, pour distinguer
// les cartes du wizard d'un coup d'œil (react-icons déjà présent partout).
const PLATFORM_STYLE = {
  meta:     { icon: <FaFacebookF />,    iconColor: "#ffffff", iconBg: "#1877f2", border: "border-l-4 border-l-[#1877f2]" },
  tiktok:   { icon: <FaTiktok />,       iconColor: "#ffffff", iconBg: "#010101", border: "border-l-4 border-l-[#f0264e]" },
  snapchat: { icon: <FaSnapchatGhost />, iconColor: "#1a1a1a", iconBg: "#FFFC00", border: "border-l-4 border-l-[#FFD400]" },
  google:   { icon: <FaGoogle />,       iconColor: "#ffffff", iconBg: "#4285f4", border: "border-l-4 border-l-[#4285f4]" },
  linkedin: { icon: <FaLinkedinIn />,   iconColor: "#ffffff", iconBg: "#0a66c2", border: "border-l-4 border-l-[#0a66c2]" },
};

// Google n'a pas de « pixel » : le suivi des conversions repose sur une paire
// ID de conversion (AW-XXXXXXXXX) + Libellé, sans authentification vérifiable
// via API. On adapte donc les libellés de champs et le texte du contrôle.
const FIELD_LABELS = {
  google: {
    id: "ID de conversion", idPlaceholder: "AW-123456789",
    token: "Libellé de conversion", tokenPlaceholder: "AbCdEfGhIj-k12LmN3op",
    reassurance: "Cette paire ne permet ni de lire vos campagnes ni de dépenser votre budget : Google ne propose pas d'API pour la vérifier en direct, nous contrôlons uniquement son format.",
    checkLabel: "Vérifier le format", checkingLabel: "Vérification…",
    successLabel: "Format valide",
  },
  linkedin: {
    id: "URN / ID de la conversion", idPlaceholder: "urn:lla:llaPartnerConversion:123456789 (ou l'ID numérique)",
    token: "Token d'accès (optionnel)", tokenPlaceholder: "Vide = réutilise votre connexion LinkedIn",
    reassurance: "Sans token collé, OneBotAds réutilise le token de votre connexion LinkedIn pour la Conversions API. Un token collé est chiffré avant d'être stocké et ne vous sera jamais réaffiché.",
    checkLabel: "Tester", checkingLabel: "Vérification…",
    successLabel: "Conversion valide",
  },
  default: {
    id: "Pixel ID", idPlaceholder: "1234567890123456",
    token: "Token API de conversions", tokenPlaceholder: "EAAG9ZB…",
    reassurance: "Ce token est limité à ce pixel : il ne peut ni lire vos campagnes ni dépenser votre budget. Il est chiffré avant d'être stocké et ne vous sera jamais réaffiché.",
    checkLabel: "Tester la connexion", checkingLabel: "Test en cours…",
    successLabel: "Connexion confirmée",
  },
};

// Où l'utilisateur trouve son Pixel ID et son token CAPI, par régie.
// Volontairement explicite : c'est l'étape où les gens décrochent.
// Si un bouton "Ouvrir Events Manager" est affiché au-dessus (compte connecté
// chez nous), l'étape 1 de chaque liste est déjà faite pour l'utilisateur —
// les guides restent quand même complets pour ceux qui n'ont pas connecté
// leurs pubs via OneBotAds et arrivent directement sur cette page.
const PIXEL_GUIDE = {
  meta: [
    "Cliquez sur « Ouvrir Meta Events Manager » ci-contre — si votre pixel est déjà détecté, le lien ouvre directement SES paramètres.",
    "Sinon : Sources de données (Data Sources) → sélectionnez votre pixel (affiché comme « dataset », l'ID ne change pas) et copiez son ID.",
    "Onglet Paramètres → section API de conversions → « Générer un token d'accès » (sous « Configuration manuelle »). Copiez-le immédiatement.",
    "Collez l'ID et le token ci-dessous, puis « Tester ». Astuce : dans la plupart des cas, OneBotAds réutilise déjà votre connexion Facebook — ne faites cette étape que si la vérification signale un échec d'envoi.",
  ],
  tiktok: [
    "Cliquez sur « Ouvrir TikTok Events Manager » ci-contre (le lien pointe déjà sur VOTRE compte publicitaire).",
    "Ouvrez votre pixel (Sources de données) → « Premiers pas » / Paramètres : copiez l'ID du pixel (ex : D9MM6L3C77UAISU2GDUG).",
    "Cliquez sur « Générer un jeton d'accès ». ⚠️ Copiez-le IMMÉDIATEMENT : TikTok ne le conserve pas — s'il est perdu, il faudra en régénérer un.",
    "Collez l'ID du pixel et le jeton ci-dessous, puis « Tester » : la validation se fait en direct auprès de TikTok.",
  ],
  snapchat: [
    "Dans Snapchat Ads Manager, ouvrez Events Manager → Paramètres et copiez l'ID de votre Pixel.",
    "Ouvrez ensuite le menu du compte → Business Details.",
    "Section OAuth Apps → Conversions API Tokens → « Generate Token » (réservé à un administrateur de l'organisation).",
    "Collez l'ID et le token ci-dessous.",
  ],
  linkedin: [
    "Cliquez sur « Ouvrir LinkedIn Events Manager » ci-contre (le lien pointe sur les Conversions de VOTRE compte Campaign Manager).",
    "Créez une conversion si vous n'en avez pas : « Créer une conversion » → type « API de conversions » → nommez-la (ex : Achat) → Enregistrer.",
    "Ouvrez la conversion créée et copiez son ID (visible dans l'URL ou la fiche — un nombre, ou l'URN complète urn:lla:llaPartnerConversion:…).",
    "Collez l'ID ci-dessous. Le token est OPTIONNEL : laissez vide pour réutiliser votre connexion LinkedIn — collez un token dédié uniquement si l'envoi échoue.",
  ],
  google: [
    "Dans Google Ads, ouvrez Outils et paramètres → Conversions (ou Objectifs → Résumé).",
    "Cliquez sur l'action de conversion à suivre (ex. « Achat »).",
    "Section Configurer la balise → « Installer vous-même ».",
    "Dans l'extrait de code affiché (AW-XXXXXXXXX/XXXXXXXXXXX), copiez l'ID de conversion (avant le /) et le libellé de conversion (après le /) ci-dessous.",
  ],
};

const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-2xl border border-gray-100 shadow-sm p-4 md:p-6 ${className}`}>{children}</div>
);

const StatusDot = ({ status }) => {
  const map = {
    done: "bg-green-500",
    skipped: "bg-gray-300",
    in_progress: "bg-amber-400",
    pending: "bg-gray-200",
  };
  return <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${map[status] || map.pending}`} />;
};

// ── Barre d'étapes : verticale sur mobile (lisible), horizontale sur desktop ──
const Stepper = ({ steps, active, onSelect }) => (
  <div className="flex flex-col md:flex-row gap-2 md:gap-3 mb-6">
    {SETUP_STEPS.map((s, i) => {
      const status = steps?.[s.key]?.status || "pending";
      const isActive = active === s.key;
      return (
        <button
          key={s.key}
          type="button"
          onClick={() => onSelect(s.key)}
          className={`flex-1 text-left rounded-xl border px-3 py-2.5 transition ${
            isActive ? "border-[#221C51] bg-[#221C51]/5" : "border-gray-200 bg-white hover:border-gray-300"
          }`}
        >
          <div className="flex items-center gap-2">
            <StatusDot status={status} />
            <span className="text-[11px] font-mono text-gray-400">{String(i + 1).padStart(2, "0")}</span>
            <span className={`text-sm font-semibold truncate ${isActive ? "text-[#221C51]" : "text-gray-600"}`}>{s.label}</span>
          </div>
          <p className="text-[11px] text-gray-400 mt-0.5 ml-[26px] truncate">{s.hint}</p>
        </button>
      );
    })}
  </div>
);

// ── Étape 1 : le site ──
const StepSite = ({ value, onSave, saving }) => {
  const [platform, setPlatform] = useState(value?.platform || "shopify");
  const [domain, setDomain] = useState(value?.domain || "");

  return (
    <Card>
      <h3 className="text-base font-bold text-[#221C51] mb-1">Où voulez-vous suivre les conversions ?</h3>
      <p className="text-sm text-gray-500 mb-5">
        Cette information détermine la méthode d'installation que nous vous proposerons à l'étape suivante.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 mb-5">
        {SITE_PLATFORMS.map((p) => (
          <button
            key={p.value}
            type="button"
            onClick={() => setPlatform(p.value)}
            className={`text-left rounded-xl border px-4 py-3 transition ${
              platform === p.value ? "border-[#221C51] bg-[#221C51]/5" : "border-gray-200 hover:border-gray-300"
            }`}
          >
            <p className="text-sm font-semibold text-[#221C51]">{p.label}</p>
            <p className="text-xs text-gray-400 mt-0.5">{p.note}</p>
          </button>
        ))}
      </div>

      <label className="block text-sm font-semibold text-[#221C51] mb-1">Domaine de la boutique</label>
      <input
        value={domain}
        onChange={(e) => setDomain(e.target.value)}
        placeholder="maboutique.com"
        className="block w-full p-2.5 text-sm rounded-lg border border-gray-300 bg-gray-50 focus:outline-none focus:border-[#221C51] mb-1"
      />
      <p className="text-xs text-gray-400 mb-5">Sans https:// — juste le domaine.</p>

      <button
        type="button"
        disabled={saving || !domain.trim()}
        onClick={() => onSave({ platform, domain: domain.trim() })}
        className="px-5 py-2.5 bg-[#221C51] text-white text-sm font-semibold rounded-xl hover:bg-[#2d2570] disabled:opacity-50 transition"
      >
        {saving ? "Enregistrement…" : "Continuer"}
      </button>
    </Card>
  );
};

// ── Étape 2 : les pixels ──
const PixelForm = ({ platform, existing, eventsManagerUrl, tutorialUrl, canAutoCreate, onSaved }) => {
  const [mode, setMode] = useState("manual");
  const [pixelId, setPixelId] = useState(existing?.pixelId || "");
  const [capiToken, setCapiToken] = useState("");
  const [name, setName] = useState(existing?.name || "");
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [check, setCheck] = useState(null);
  const fieldLabels = FIELD_LABELS[platform] || FIELD_LABELS.default;

  const runValidate = async () => {
    setChecking(true); setCheck(null);
    try {
      const r = await validateSetupPixel({ platform, pixelId: pixelId.trim(), capiToken: capiToken.trim() });
      setCheck(r);
      if (r.valid && r.name && !name) setName(r.name);
    } catch (e) {
      setCheck({ valid: false, error: e?.response?.data?.message || e.message });
    } finally { setChecking(false); }
  };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = mode === "manual"
        ? { platform, mode, pixelId: pixelId.trim(), capiToken: capiToken.trim(), name: name.trim() || undefined }
        : { platform, mode, name: name.trim() };
      const res = await saveSetupPixel(payload);
      toast.success(`Pixel ${PLATFORM_LABEL[platform]} configuré.`);
      onSaved(res.setup);
      setCapiToken("");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Configuration du pixel impossible.");
    } finally { setSaving(false); }
  };

  const canSubmit = mode === "manual"
    // LinkedIn : token optionnel (repli sur la connexion LinkedIn à l'envoi).
    ? pixelId.trim() && (platform === "linkedin" || capiToken.trim())
    : name.trim();

  const style = PLATFORM_STYLE[platform] || {};
  return (
    <div className={`border border-gray-200 rounded-xl p-4 ${style.border || ""}`}>
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <span className="flex items-center gap-2.5 min-w-0">
          <span
            className="w-8 h-8 rounded-lg flex items-center justify-center text-sm flex-shrink-0 shadow-sm"
            style={{ backgroundColor: style.iconBg, color: style.iconColor }}
          >
            {style.icon}
          </span>
          <p className="text-sm font-bold text-[#221C51]">{PLATFORM_LABEL[platform]}</p>
        </span>
        {existing && (
          <span className="text-[11px] font-semibold bg-green-50 text-green-700 px-2.5 py-1 rounded-full">
            Configuré · {existing.pixelId}
          </span>
        )}
      </div>

      {canAutoCreate !== false && (
        <div className="join w-full mb-4">
          {[
            { k: "manual", l: "Coller un pixel existant" },
            { k: "auto", l: "En créer un nouveau" },
          ].map((o) => (
            <button
              key={o.k}
              type="button"
              onClick={() => { setMode(o.k); setCheck(null); }}
              className={`join-item btn btn-sm flex-1 font-medium border-gray-300 ${
                mode === o.k ? "bg-[#221C51] hover:bg-[#221C51] text-white border-[#221C51]" : "bg-white text-[#221C51]"
              }`}
            >
              {o.l}
            </button>
          ))}
        </div>
      )}

      {mode === "manual" ? (
        <>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
            <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
              <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500">Où trouver ces valeurs</p>
              <span className="flex items-center gap-1.5 flex-wrap">
                {tutorialUrl && (
                  <a href={tutorialUrl} target="_blank" rel="noreferrer"
                    className="text-[11px] font-semibold text-fuchsia-700 bg-fuchsia-50 border border-fuchsia-200 px-2.5 py-1 rounded-full hover:bg-fuchsia-100 transition whitespace-nowrap">
                    🎥 Voir le tuto
                  </a>
                )}
                {eventsManagerUrl && (
                  <a href={eventsManagerUrl} target="_blank" rel="noreferrer"
                    className="text-[11px] font-semibold text-white bg-[#221C51] px-2.5 py-1 rounded-full hover:bg-[#2d2570] transition whitespace-nowrap">
                    Ouvrir {PLATFORM_LABEL[platform]} Events Manager ↗
                  </a>
                )}
              </span>
            </div>
            <ol className="text-xs text-gray-600 space-y-1 list-decimal list-inside">
              {PIXEL_GUIDE[platform].map((s, i) => <li key={i}>{s}</li>)}
            </ol>
            <p className="text-[11px] text-gray-500 mt-2.5 leading-relaxed">
              {fieldLabels.reassurance}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs font-semibold text-[#221C51] mb-1">{fieldLabels.id}</label>
              <input value={pixelId} onChange={(e) => { setPixelId(e.target.value); setCheck(null); }}
                placeholder={fieldLabels.idPlaceholder}
                className="block w-full p-2 text-sm rounded-lg border border-gray-300 bg-gray-50 focus:outline-none focus:border-[#221C51]" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#221C51] mb-1">{fieldLabels.token}</label>
              <input type="password" value={capiToken} onChange={(e) => { setCapiToken(e.target.value); setCheck(null); }}
                placeholder={fieldLabels.tokenPlaceholder}
                className="block w-full p-2 text-sm rounded-lg border border-gray-300 bg-gray-50 focus:outline-none focus:border-[#221C51]" />
            </div>
          </div>

          <div className="mb-3">
            <label className="block text-xs font-semibold text-[#221C51] mb-1">Nom <span className="font-normal text-gray-400">(optionnel)</span></label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Pixel boutique"
              className="block w-full p-2 text-sm rounded-lg border border-gray-300 bg-gray-50 focus:outline-none focus:border-[#221C51]" />
          </div>

          {check && (
            <div className={`rounded-lg px-3 py-2 text-xs mb-3 ${check.valid ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"}`}>
              {check.valid
                ? <>{fieldLabels.successLabel}{check.name ? ` — pixel « ${check.name} »` : ""}.</>
                : <>Échec : {check.error}</>}
            </div>
          )}

          <div className="flex gap-2 flex-wrap">
            <button type="button" onClick={runValidate} disabled={checking || !pixelId.trim() || !capiToken.trim()}
              className="px-4 py-2 bg-white text-[#221C51] text-sm font-semibold rounded-lg border border-[#221C51]/25 hover:bg-gray-50 disabled:opacity-50 transition">
              {checking ? fieldLabels.checkingLabel : fieldLabels.checkLabel}
            </button>
            <button type="button" onClick={submit} disabled={saving || !canSubmit}
              className="px-4 py-2 bg-[#221C51] text-white text-sm font-semibold rounded-lg hover:bg-[#2d2570] disabled:opacity-50 transition">
              {saving ? "Enregistrement…" : "Enregistrer"}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
            <p className="text-xs text-amber-800 leading-relaxed">
              À réserver aux comptes neufs. Si vous diffusez déjà des publicités, votre pixel actuel
              porte l'historique d'apprentissage de l'algorithme — en créer un nouveau repartirait de zéro.
            </p>
          </div>
          <label className="block text-xs font-semibold text-[#221C51] mb-1">Nom du nouveau pixel</label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Pixel OneBotAds"
            className="block w-full p-2 text-sm rounded-lg border border-gray-300 bg-gray-50 focus:outline-none focus:border-[#221C51] mb-3" />
          <button type="button" onClick={submit} disabled={saving || !canSubmit}
            className="px-4 py-2 bg-[#221C51] text-white text-sm font-semibold rounded-lg hover:bg-[#2d2570] disabled:opacity-50 transition">
            {saving ? "Création…" : "Créer le pixel"}
          </button>
        </>
      )}
    </div>
  );
};

const StepPixels = ({ context, onSaved, onSkip }) => (
  <Card>
    <h3 className="text-base font-bold text-[#221C51] mb-1">Reliez vos pixels de conversion</h3>
    <p className="text-sm text-gray-500 mb-5">
      Un pixel par régie sur laquelle vous diffusez. Configurez au moins une plateforme pour continuer —
      vous pourrez ajouter les autres plus tard.
    </p>

    <div className="space-y-4">
      {(context?.pixelPlatforms || []).map((p) => (
        <PixelForm key={p.platform} platform={p.platform} existing={p.pixel} eventsManagerUrl={p.eventsManagerUrl} tutorialUrl={p.tutorialUrl}
          canAutoCreate={p.canAutoCreate} onSaved={onSaved} />
      ))}
    </div>

    <button type="button" onClick={onSkip} className="mt-5 text-sm text-gray-400 hover:text-[#221C51] underline transition">
      Passer cette étape pour l'instant
    </button>
  </Card>
);

// ── Étape 3 : les événements ──
const StepEvents = ({ value, onSave, saving }) => {
  const [enabled, setEnabled] = useState(value?.enabled || ["pageview", "addtocart", "purchase"]);

  const toggle = (v) => {
    const item = EVENT_CATALOG.find((e) => e.value === v);
    if (item?.always) return; // pageview est toujours collecté
    setEnabled((cur) => (cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]));
  };

  return (
    <Card>
      <h3 className="text-base font-bold text-[#221C51] mb-1">Que voulez-vous mesurer ?</h3>
      <p className="text-sm text-gray-500 mb-5">
        Ces événements seront collectés sur votre site et transmis aux régies en server-to-server.
        L'achat est indispensable au calcul du ROAS.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 mb-5">
        {EVENT_CATALOG.map((e) => {
          const on = enabled.includes(e.value);
          return (
            <button
              key={e.value}
              type="button"
              onClick={() => toggle(e.value)}
              disabled={e.always}
              className={`flex items-center justify-between gap-3 text-left rounded-xl border px-4 py-3 transition ${
                on ? "border-[#221C51] bg-[#221C51]/5" : "border-gray-200 hover:border-gray-300"
              } ${e.always ? "opacity-70 cursor-default" : ""}`}
            >
              <span>
                <span className="block text-sm font-semibold text-[#221C51]">{e.label}</span>
                <span className="block text-[11px] text-gray-400 font-mono">{e.value}</span>
              </span>
              <span className="flex items-center gap-1.5 flex-shrink-0">
                {e.critical && <span className="text-[10px] font-semibold text-amber-600">requis ROAS</span>}
                {e.always && <span className="text-[10px] text-gray-400">toujours actif</span>}
                <StatusDot status={on ? "done" : "pending"} />
              </span>
            </button>
          );
        })}
      </div>

      <button type="button" disabled={saving || enabled.length === 0} onClick={() => onSave(enabled)}
        className="px-5 py-2.5 bg-[#221C51] text-white text-sm font-semibold rounded-xl hover:bg-[#2d2570] disabled:opacity-50 transition">
        {saving ? "Enregistrement…" : "Continuer"}
      </button>
    </Card>
  );
};

// ── Étape 4 : vérification ──
const StepVerify = ({ setup, onVerified }) => {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard?.writeText(setup.install);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const run = async () => {
    setRunning(true);
    try {
      const r = await verifySetup();
      setResult(r);
      onVerified(r.setup);
      if (r.pixelInstalled) toast.success("Événements reçus — le suivi fonctionne.");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Vérification impossible.");
    } finally { setRunning(false); }
  };

  return (
    <Card>
      <h3 className="text-base font-bold text-[#221C51] mb-1">Installez le suivi, puis vérifiez</h3>
      <p className="text-sm text-gray-500 mb-4">
        Collez ce code dans le <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">&lt;head&gt;</code> de
        votre site, visitez une page, puis lancez le test.
      </p>

      <div className="relative mb-5">
        <pre className="text-[11px] bg-[#0F0B2D] text-green-300 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap">{setup.install}</pre>
        <button type="button" onClick={copy}
          className="absolute top-2 right-2 text-[10px] bg-white/10 text-white px-2 py-1 rounded hover:bg-white/20 transition">
          {copied ? "Copié" : "Copier"}
        </button>
      </div>

      <button type="button" onClick={run} disabled={running}
        className="px-5 py-2.5 bg-[#221C51] text-white text-sm font-semibold rounded-xl hover:bg-[#2d2570] disabled:opacity-50 transition mb-5">
        {running ? "Analyse en cours…" : "Lancer le test"}
      </button>

      {result && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
            {[
              { l: "Événements reçus", v: result.eventsTotal },
              { l: "Dernières 24 h", v: result.eventsLast24h },
              { l: "Achats", v: result.conversions },
              { l: "Mode CAPI", v: result.capiMode },
            ].map((k) => (
              <div key={k.l} className="rounded-xl bg-gray-50 border border-gray-200 p-3 min-w-0">
                <p className="text-[10px] uppercase font-bold tracking-wide text-gray-400 truncate">{k.l}</p>
                <p className="text-lg font-bold text-[#221C51] mt-0.5 break-words">{k.v}</p>
              </div>
            ))}
          </div>

          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">Envoi server-to-server</p>
            <div className="flex flex-wrap gap-2">
              {result.platforms.map((p) => (
                <span key={p.platform}
                  className={`text-[11px] font-medium px-2.5 py-1 rounded-full border ${
                    p.configured ? "bg-green-50 text-green-700 border-green-200" : "bg-gray-50 text-gray-400 border-gray-200"
                  }`}>
                  {p.platform} {p.configured ? `· ${p.tokenSource === "pixel" ? "token pixel" : "token Ads"}` : "· non configuré"}
                </span>
              ))}
            </div>
          </div>

          {result.warnings?.length > 0 && (
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3.5">
              <p className="text-xs font-bold text-amber-800 mb-1.5">À corriger</p>
              <ul className="text-xs text-amber-800 space-y-1 list-disc list-inside">
                {result.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}

          {result.pixelInstalled && result.warnings.length === 0 && (
            <div className="rounded-xl bg-green-50 border border-green-200 p-3.5">
              <p className="text-sm font-semibold text-green-800">Configuration terminée.</p>
              <p className="text-xs text-green-700 mt-0.5">
                Les conversions remontent et sont transmises aux régies. Le ROAS s'affichera dans Tracking
                dès les premiers achats attribués.
              </p>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

// ── Composant principal ──
const TrackingSetup = () => {
  const [setup, setSetup] = useState(null);
  const [context, setContext] = useState(null);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, c] = await Promise.all([fetchSetup(), fetchSetupContext()]);
      setSetup(s);
      setContext(c);
      // Reprise : on ouvre la première étape non terminée, sauf si l'utilisateur
      // navigue déjà manuellement dans le parcours.
      setActive((cur) => cur || (s.resumeStep === "done" ? "verify" : s.resumeStep));
    } catch (e) {
      toast.error("Chargement de la configuration impossible.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const applySetup = (s) => { setSetup(s); fetchSetupContext().then(setContext).catch(() => {}); };

  const handleSite = async (payload) => {
    setSaving(true);
    try { applySetup(await saveSetupSite(payload)); setActive("pixels"); toast.success("Site enregistré."); }
    catch (e) { toast.error(e?.response?.data?.message || "Enregistrement impossible."); }
    finally { setSaving(false); }
  };

  const handleEvents = async (enabled) => {
    setSaving(true);
    try { applySetup(await saveSetupEvents(enabled)); setActive("verify"); toast.success("Événements enregistrés."); }
    catch (e) { toast.error(e?.response?.data?.message || "Enregistrement impossible."); }
    finally { setSaving(false); }
  };

  const handleSkip = async (step) => {
    try { applySetup(await skipSetupStep(step)); toast.info("Étape passée."); setActive("events"); }
    catch { toast.error("Impossible de passer cette étape."); }
  };

  const handleReset = async () => {
    if (!window.confirm("Réinitialiser le parcours ? Vos pixels déjà enregistrés seront conservés.")) return;
    try { const s = await resetSetup(); setSetup(s); setActive("site"); toast.info("Parcours réinitialisé."); }
    catch { toast.error("Réinitialisation impossible."); }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20 text-gray-400 text-sm">Chargement de la configuration…</div>;
  }
  if (!setup) return null;

  return (
    <div className="p-3 md:p-6 max-w-4xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-[#221C51]">Configuration du tracking</h2>
          <p className="text-sm text-gray-400 mt-0.5">
            {setup.completedAt
              ? "Configuration terminée — vous pouvez la modifier à tout moment."
              : "Vos réponses sont enregistrées à chaque étape : vous pouvez reprendre plus tard."}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-[11px] uppercase font-bold tracking-wide text-gray-400">Progression</p>
            <p className="text-sm font-bold text-[#221C51]">{setup.progress.done} / {setup.progress.total} étapes</p>
          </div>
          <div className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ background: `conic-gradient(${BRAND} ${setup.progress.percent * 3.6}deg, #E9E7F2 0deg)` }}>
            <span className="w-11 h-11 rounded-full bg-white flex items-center justify-center text-xs font-bold text-[#221C51]">
              {setup.progress.percent}%
            </span>
          </div>
        </div>
      </div>

      <Stepper steps={setup.steps} active={active} onSelect={setActive} />

      {active === "site" && <StepSite value={setup.steps.site} onSave={handleSite} saving={saving} />}
      {active === "pixels" && <StepPixels context={context} onSaved={applySetup} onSkip={() => handleSkip("pixels")} />}
      {active === "events" && <StepEvents value={setup.steps.events} onSave={handleEvents} saving={saving} />}
      {active === "verify" && <StepVerify setup={setup} onVerified={applySetup} />}

      <button type="button" onClick={handleReset}
        className="mt-6 text-xs text-gray-400 hover:text-red-500 underline transition">
        Réinitialiser le parcours
      </button>
    </div>
  );
};

export default TrackingSetup;
